/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { ExternalLinkIcon } from "lucide-react"
import type { ComponentProps } from "react"

import { cn } from "@/utils/cn"

import { buttonVariants } from "../primitives/button"

interface Props extends ComponentProps<"a"> {
    /**
     * Absolute URL outside the console.
     */
    href: string
}

/**
 * Renders an external link as a small outline button.
 *
 * Opens in a new tab and marks this in both the icon and accessible text.
 * Remaining anchor props are passed through, and `className` can override the
 * default button styles.
 *
 * `target` and `rel` are fixed so the link always has the behavior announced
 * to screen readers.
 *
 * @example
 * <ExternalLink href="https://www.virustotal.com/">VirusTotal</ExternalLink>
 */
export const ExternalLink = ({ className, children, ...props }: Props) => (
    <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        // Keep the link's appearance consistent with the button primitives during the Bootstrap migration.
        className={cn(buttonVariants({ variant: "outline", size: "sm" }), "text-foreground no-underline", className)}
    >
        {children}
        <ExternalLinkIcon data-icon="inline-end" />
        <span className="sr-only"> (opens in a new tab)</span>
    </a>
)
